import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import {
  getProducts,
  getVillagerBaskets,
  getVillagerFarm,
  getVillagerOrders,
} from "../Utils/storeService";

const FarmContext = createContext(null);

const EMPTY = {
  farm: null,
  orders: [],
  products: [],
  baskets: [],
  loading: false,
  noFarm: false,
  isFarmer: false,
};

const noop = () => {};

/**
 * The farm's own data, hoisted out of the portal so the rest of the app can see
 * it. The navbar needs the task count to badge the way into the portal, and a
 * farmer who is browsing the shop has no other signal that an order is about to
 * auto-cancel. Keeping it here also means the portal is not the only screen
 * allowed to know what a farm holds.
 *
 * Inert for anyone who is not an approved Villager: no fetch, no state, so the
 * shopper-side navbar costs nothing.
 */
export const FarmProvider = ({ children }) => {
  const { token, userRole } = useAuth();
  const isFarmer = Boolean(token) && userRole === "Villager";

  const [farm, setFarm] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [baskets, setBaskets] = useState([]);
  const [loading, setLoading] = useState(isFarmer);
  const [noFarm, setNoFarm] = useState(false);

  const refresh = useCallback(async () => {
    if (!isFarmer) return null;
    setLoading(true);
    try {
      const farmData = await getVillagerFarm(token);
      setFarm(farmData);
      setNoFarm(false);

      // Each call is isolated so one failing endpoint (or a stubbed service in a
      // test) cannot blank the whole farm.
      const safely = (call, fallback) =>
        Promise.resolve()
          .then(call)
          .catch(() => fallback);

      const [ordersData, productsData, basketsData] = await Promise.all([
        safely(() => getVillagerOrders(token), []),
        safely(() => getProducts(farmData.id), []),
        safely(() => getVillagerBaskets(token), []),
      ]);

      setOrders(ordersData || []);
      setProducts(productsData || []);
      setBaskets(basketsData || []);
      return farmData;
    } catch (err) {
      // 404 means the account is approved but has never registered a farm — the
      // portal turns this into the onboarding redirect.
      if (err?.response?.status === 404) setNoFarm(true);
      return null;
    } finally {
      setLoading(false);
    }
  }, [isFarmer, token]);

  /**
   * Takes a farm the caller just created and treats it as current.
   *
   * Registration returns the new farm, so the portal should never have to go and
   * ask whether it exists. Without this the onboarding wizard navigated to a
   * portal still holding the 404 it got before the farm was created, which read
   * as "you have no farm" and bounced the farmer straight back to the wizard
   * they had just finished.
   */
  const adoptFarm = useCallback(
    (farmData) => {
      if (!farmData) return;
      setFarm(farmData);
      setNoFarm(false);
      // Its produce and baskets are fetched in the background; an empty list is
      // the truth for a farm created seconds ago either way.
      refresh();
    },
    [refresh]
  );

  useEffect(() => {
    if (!isFarmer) {
      setFarm(null);
      setOrders([]);
      setProducts([]);
      setBaskets([]);
      setNoFarm(false);
      setLoading(false);
      return;
    }
    refresh();
  }, [isFarmer, refresh]);

  const value = useMemo(
    () => ({
      isFarmer,
      farm,
      orders,
      products,
      baskets,
      loading,
      noFarm,
      setFarm,
      setOrders,
      setProducts,
      setBaskets,
      refresh,
      adoptFarm,
    }),
    [isFarmer, farm, orders, products, baskets, loading, noFarm, refresh, adoptFarm]
  );

  return <FarmContext.Provider value={value}>{children}</FarmContext.Provider>;
};

/**
 * Safe outside the provider — components that render for shoppers too (navbar)
 * get the inert shape rather than a crash.
 */
export const useFarm = () => {
  const ctx = useContext(FarmContext);
  if (!ctx) {
    return {
      ...EMPTY,
      setFarm: noop,
      setOrders: noop,
      setProducts: noop,
      setBaskets: noop,
      refresh: async () => null,
      adoptFarm: noop,
    };
  }
  return ctx;
};

export default FarmContext;
